// Central registry for deep links out of the accounting app.
//
// Purely accounting views live here. Anything that belongs to another app
// (CRM contacts, operations/inventory, HR/payroll, platform admin, the
// document store, the customer portal) is a card that links out — never an
// embedded view. Every card goes through `buildDeepLink`, which passes the
// active organization scope through as a query param and, when the target
// origin is unconfigured (`VITE_*_ORIGIN` empty), returns `href: null` with
// an explanation so the card renders disabled — never a dead link.
//
// UI-WORDING RULE: card titles and bodies use plain language (Who owes you,
// What you owe, Payment reminders, Supplier credits, Reconcile,
// Starting balances, Spread-out income, Landed costs). Technical terms
// (AR/AP, GRNI, 3-way match, debit/credit, amortization, deferred revenue)
// appear in code identifiers and muted helper text only.

/** Cross-app targets reachable from accounting cards. */
export type CrossAppTarget =
  | "crm"
  | "ops"
  | "hrPayroll"
  | "platformAdmin"
  | "documents"
  | "portal";

const TARGET_ORIGINS: Record<CrossAppTarget, string | undefined> = {
  crm: import.meta.env.VITE_CRM_ORIGIN,
  ops: import.meta.env.VITE_OPS_ORIGIN,
  hrPayroll: import.meta.env.VITE_HR_PAYROLL_ORIGIN,
  platformAdmin: import.meta.env.VITE_PLATFORM_ADMIN_ORIGIN,
  documents: import.meta.env.VITE_DOCUMENTS_ORIGIN,
  portal: import.meta.env.VITE_PORTAL_ORIGIN,
};

const TARGET_ENV_NAMES: Record<CrossAppTarget, string> = {
  crm: "VITE_CRM_ORIGIN",
  ops: "VITE_OPS_ORIGIN",
  hrPayroll: "VITE_HR_PAYROLL_ORIGIN",
  platformAdmin: "VITE_PLATFORM_ADMIN_ORIGIN",
  documents: "VITE_DOCUMENTS_ORIGIN",
  portal: "VITE_PORTAL_ORIGIN",
};

/** Default plain-language card copy per target. Callers may override. */
const TARGET_COPY: Record<CrossAppTarget, { label: string; detail: string }> = {
  crm: {
    label: "Customer details",
    detail: "Names, addresses, and contact history live in the CRM.",
  },
  ops: {
    label: "Stock and operations",
    detail: "Quantities, warehouses, and landed costs live in operations.",
  },
  hrPayroll: {
    label: "People and pay",
    detail: "Team members and pay runs live in HR and payroll.",
  },
  platformAdmin: {
    label: "Organization administration",
    detail: "Members, roles, and billing live in SYLVIQ Console.",
  },
  documents: {
    label: "Files and receipts",
    detail: "Uploaded receipts and source files live in the document store.",
  },
  portal: {
    label: "Customer portal",
    detail: "The shareable link your customer uses to view and pay.",
  },
};

export interface DeepLinkScope {
  organization_id?: string;
}

interface DeepLink {
  target: CrossAppTarget;
  /** Plain-language card title. */
  label: string;
  /** One plain sentence saying where the link goes and why. */
  detail: string;
  /** Absolute URL, or null when the target origin is unconfigured. */
  href: string | null;
  configured: boolean;
  /** Human-readable reason shown on the disabled card when unconfigured. */
  unavailableReason: string | null;
}

interface BuildDeepLinkOptions {
  /** Path inside the target app, e.g. `/contacts/abc`. Defaults to `/`. */
  path?: string;
  /** Extra query params merged after the scope passthrough. */
  query?: Record<string, string | undefined>;
  scope?: DeepLinkScope;
  /** Override the default card copy for this usage. */
  label?: string;
  detail?: string;
}

/**
 * Builds a scoped deep link to another app. The organization scope passes
 * through as a query param so the target opens in the same organization.
 * Returns `href: null` — never a dead or relative link — when the target
 * origin env var is empty.
 */
export function buildDeepLink(
  target: CrossAppTarget,
  options: BuildDeepLinkOptions = {},
): DeepLink {
  const copy = TARGET_COPY[target];
  const label = options.label ?? copy.label;
  const detail = options.detail ?? copy.detail;
  const origin = (TARGET_ORIGINS[target] ?? "").trim().replace(/\/+$/, "");

  if (!origin) {
    return {
      target,
      label,
      detail,
      href: null,
      configured: false,
      unavailableReason: `This link is not connected yet — set ${TARGET_ENV_NAMES[target]} to enable it.`,
    };
  }

  const path = options.path ?? "/";
  const url = new URL(`${origin}${path.startsWith("/") ? path : `/${path}`}`);
  const scope = options.scope ?? {};
  if (scope.organization_id)
    url.searchParams.set("organization_id", scope.organization_id);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, value);
  }

  return {
    target,
    label,
    detail,
    href: url.toString(),
    configured: true,
    unavailableReason: null,
  };
}
